/**
 * One passive scroll listener and one rAF loop, shared by every scroll-driven effect.
 *
 * Subscribers get called at most once per frame while the page is moving, and the loop parks
 * itself once scrolling stops. Effects are expected to write to the DOM directly from their
 * callback rather than setting React state — at 449 photos, a re-render per frame is the whole
 * ballgame.
 */

import { addScrollCallbackMs } from './metrics';

type ScrollTick = (scrollY: number) => void;

const subscribers = new Set<ScrollTick>();

let running = false;
let listenerAttached = false;
let lastY = Number.NaN;
let idleFrames = 0;
let velocity = 0;

/** Keep ticking briefly after motion stops so momentum scrolling settles cleanly. */
const IDLE_FRAMES_BEFORE_PARK = 20;

function tick() {
  const y = window.scrollY;

  velocity = Number.isNaN(lastY) ? 0 : Math.abs(y - lastY);

  if (y === lastY) idleFrames += 1;
  else {
    idleFrames = 0;
    lastY = y;
  }

  const started = performance.now();
  for (const subscriber of subscribers) subscriber(y);
  addScrollCallbackMs(performance.now() - started);

  if (subscribers.size === 0 || idleFrames > IDLE_FRAMES_BEFORE_PARK) {
    running = false;
    return;
  }
  requestAnimationFrame(tick);
}

function wake() {
  if (running || subscribers.size === 0) return;
  running = true;
  idleFrames = 0;
  requestAnimationFrame(tick);
}

function attachListener() {
  if (listenerAttached) return;
  listenerAttached = true;
  window.addEventListener('scroll', wake, { passive: true });
  window.addEventListener('resize', wake, { passive: true });
}

export function subscribeToScroll(callback: ScrollTick): () => void {
  attachListener();
  subscribers.add(callback);
  callback(window.scrollY);
  wake();

  return () => {
    subscribers.delete(callback);
  };
}

/** Pixels moved in the last frame. Used to hold off expensive work during a fling. */
export function scrollVelocity(): number {
  return running ? velocity : 0;
}

/** For the dev perf HUD. */
export function scrollLoopStats(): { subscribers: number; running: boolean } {
  return { subscribers: subscribers.size, running };
}
